import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

// Page transition wrapper
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Animated section component
function AnimatedSection({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function ImmersiveExperiences() {
  return (
    <motion.div
      className="immersive-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {/* Page Hero - Playful Typography */}
      <section className="page-hero playful">
        <div className="container">
          <motion.div
            className="page-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="page-label">Portfolio</span>
            <h1 className="page-title playful-title">
              <span className="title-line">Immersive</span>
              <span className="title-line title-italic">Experiences</span>
            </h1>
            <p className="page-subtitle">
              Physical worlds that tell stories, invite exploration, and create <em>unforgettable</em> moments.
            </p>
          </motion.div>
        </div>
        <div className="hero-decoration" />
      </section>

      {/* Murder! In Wonderland - Featured Project */}
      <section className="project-feature">
        <div className="container">
          <AnimatedSection className="project-feature-header playful">
            <div className="header-with-icons">
              <div className="header-text">
                <motion.span
                  className="floating-tag"
                  initial={{ rotate: -3, opacity: 0 }}
                  whileInView={{ rotate: -3, opacity: 1 }}
                  viewport={{ once: true }}
                >
                  Featured Project
                </motion.span>
                <h2>
                  Murder!<br/>
                  <span className="title-italic">In Wonderland</span>
                </h2>
                <p className="project-tagline">
                  An Immersive Mini Golf <em>Murder Mystery</em>
                </p>
              </div>

              {/* Overlapping tool icons */}
              <motion.div
                className="tool-icons-stack"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <img src="/images/mini-golf/Ai-icon.png" alt="Adobe Illustrator" className="tool-icon icon-1" />
                <img src="/images/mini-golf/Ps-icon.webp" alt="Photoshop" className="tool-icon icon-2" />
                <img src="/images/mini-golf/Sheet-icon.png" alt="Sheets" className="tool-icon icon-3" />
              </motion.div>
            </div>
          </AnimatedSection>

          <div className="project-meta-scattered">
            <motion.div
              className="meta-card meta-1"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="meta-label">Role</span>
              <span className="meta-value">Project Lead, Design & Fabrication</span>
            </motion.div>
            <motion.div
              className="meta-card meta-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <span className="meta-label">Mentorship</span>
              <span className="meta-value">Joe Garlington, <em>Disney Imagineer</em></span>
            </motion.div>
            <motion.div
              className="meta-card meta-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <span className="meta-label">Timeline</span>
              <span className="meta-value">January - May 2023</span>
            </motion.div>
          </div>

          {/* Hero Image */}
          <AnimatedSection className="project-hero-image" delay={0.1}>
            <img
              src="/images/mini-golf/hero.png"
              alt="Murder! In Wonderland - 22-foot Castle Centerpiece"
              className="hero-image"
            />
          </AnimatedSection>

          {/* Project Overview - Screenplay Style */}
          <AnimatedSection className="screenplay-section" delay={0.2}>
            <div className="screenplay-page">
              <div className="screenplay-header">
                <span className="screenplay-title">MURDER! IN WONDERLAND</span>
                <span className="screenplay-subtitle">A Mini Golf Murder Mystery Experience</span>
              </div>

              <div className="screenplay-content">
                <p className="scene-heading">EXT. USC GREEN COURTYARD - DAY</p>
                <p className="action">
                  An empty green courtyard at the University of Southern California.
                  In eight weeks, a 14-person team will transform this space for the
                  USC GAMES EXPO AND GRADUATION CEREMONY, complete with a 22-FOOT
                  CASTLE that guests will discover around a corner.
                </p>

                <p className="scene-heading">THE CONCEPT</p>
                <p className="action">
                  Nine themed holes. Nine suspects. One murder to solve. Guests enter
                  through an oversized RABBIT HOLE and receive scorecards that double
                  as CLUE SHEETS. Each hole reveals a new WONDERLAND CHARACTER as
                  a potential suspect, with hidden clues woven into every installation.
                </p>

                <p className="character-intro">
                  <span className="character-name">THE MAD HATTER</span>: Our victim.
                  Found at the second hole, poisoned at his tea party.
                </p>

                <p className="scene-heading">THE FINAL HOLE</p>
                <p className="action">
                  At the ninth hole, players make their ACCUSATION, choosing who they
                  believe committed the murder. Then comes the GRAND REVEAL: the true
                  killer is unmasked, and all the clues hidden throughout the course
                  suddenly click into place.
                </p>

                <p className="parenthetical">
                  (guests can replay the course, now seeing the clues they may have missed –
                  a completely different experience the second time through)
                </p>
              </div>
            </div>
          </AnimatedSection>

          {/* Feature Images */}
          <AnimatedSection className="feature-images-layout" delay={0.2}>
            <div className="feature-images-5grid">
              <div className="feature-stack feature-stack-left">
                <div className="feature-img-sm">
                  <img src="/images/mini-golf/Castle-1.png" alt="Castle construction" />
                </div>
                <div className="feature-img-sm">
                  <img src="/images/mini-golf/Castle-2.png" alt="Castle detail" />
                </div>
              </div>
              <div className="feature-img-hero">
                <span className="feature-tag">22-ft tall</span>
                <img src="/images/mini-golf/Castle-Built.png" alt="The 22-foot castle centerpiece" />
              </div>
              <div className="feature-stack feature-stack-right">
                <div className="feature-img-sm contain-img">
                  <img src="/images/mini-golf/Castle-3.png" alt="Castle lighting" />
                </div>
                <div className="feature-img-sm">
                  <img src="/images/mini-golf/Castle-4.png" alt="Castle reveal" />
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Content Sections */}
          <div className="project-content-grid">
            <AnimatedSection className="content-section" delay={0.1}>
              <h3>The Challenge</h3>
              <ul className="content-list">
                <li>Design and fabricate nine themed holes that told a cohesive murder mystery story</li>
                <li>Build a show-stopping 22-foot castle centerpiece</li>
                <li>Create interactive elements that engaged guests beyond just playing mini golf</li>
                <li>Manage complex production logistics across fabrication, installation, and live events</li>
                <li>Deliver an experience worthy of the themed entertainment industry's standards</li>
              </ul>
            </AnimatedSection>

            <AnimatedSection className="content-section" delay={0.2}>
              <h3>Design Approach</h3>
              <p>
                Leading this project with mentorship from Disney Imagineer Joe Garlington,
                I focused on creating "weenies" (iconic visual landmarks) that would draw
                guests through the space while maintaining narrative coherence.
              </p>
              <div className="highlight-box">
                <h4>Key Design Decisions</h4>
                <ul className="content-list">
                  <li>The 22-foot castle became our primary weenie, visible from multiple holes and serving as the murder mystery's central location</li>
                  <li>Each hole represented a different Wonderland character as a "suspect," with environmental storytelling clues</li>
                  <li>Holes 1, 2, and 3 became my signature pieces, setting the scene of the crime and building the world for players. The first hole plays like traditional mini golf to ease guests in. The second places players atop the Mad Hatter's tea party table where the murder occurred. The third has players working together on a circular tilting table, using their mallets as levers to navigate balls through a liquid maze to the correct exit</li>
                  <li>Color-coded pathways guided guests through the story while maintaining clear wayfinding</li>
                </ul>
              </div>
            </AnimatedSection>

            <AnimatedSection className="content-section full-width" delay={0.3}>
              <h3>Fabrication & Technical Execution</h3>
              <p>
                I led the fabrication process for multiple holes, translating sketches and
                scale models into buildable structures.
              </p>
              <div className="skills-showcase">
                <div className="skill-card">
                  <div className="skill-icon">&#9670;</div>
                  <h4>Technical Drafting</h4>
                  <p>Translated rough sketches into production-ready vector files, scaled, nested, and optimized for CNC and laser cutting</p>
                </div>
                <div className="skill-card">
                  <div className="skill-icon">&#9670;</div>
                  <h4>Scale Modeling</h4>
                  <p>1:12 scale tests for proportions and sightlines</p>
                </div>
                <div className="skill-card">
                  <div className="skill-icon">&#9670;</div>
                  <h4>CNC Routing & Laser Cutting</h4>
                  <p>300+ custom-cut dimensional pieces</p>
                </div>
                <div className="skill-card">
                  <div className="skill-icon">&#9670;</div>
                  <h4>Finishing & Assembly</h4>
                  <p>Primed, painted, and assembled pieces for final installations</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="content-section" delay={0.4}>
              <h3>The Guest Experience</h3>
              <p>
                Guests entered through an oversized "rabbit hole" and received scorecards
                that doubled as murder mystery clue sheets.
              </p>
              <ul className="content-list">
                <li>Character-specific environments with hidden storytelling details</li>
                <li>Interactive elements like spinning roses, glowing mushrooms, and trick mirrors</li>
                <li>The dramatic reveal of the 22-foot castle, lit from within</li>
                <li>A finale hole where guests submitted their "solution" to the murder</li>
              </ul>
              <p className="insight-text">
                The experience worked on multiple levels: casual players enjoyed a whimsical
                mini golf game, while engaged guests uncovered a layered narrative mystery.
              </p>
            </AnimatedSection>

            <AnimatedSection className="content-section" delay={0.5}>
              <h3>Production Leadership</h3>
              <p>As project lead, I managed our 14-person team through:</p>
              <ul className="content-list">
                <li>Daily production schedules and milestone tracking</li>
                <li>Resource allocation across four fabrication stations</li>
                <li>Quality control and safety protocols</li>
                <li>Installation coordination with venue technical staff</li>
                <li>Opening night event management</li>
                <li>Regular check-ins with mentor Joe Garlington for design review</li>
              </ul>
            </AnimatedSection>
          </div>

          {/* Image Gallery */}
          <AnimatedSection className="project-gallery" delay={0.3}>
            <h3>Project Gallery</h3>
            <div className="gallery-grid">
              <motion.div
                className="gallery-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <img src="/images/mini-golf/MiniGolf-scalemodel.png" alt="Scale model of the mini golf course" />
                <span className="gallery-caption">Scale Model</span>
              </motion.div>
              <motion.div
                className="gallery-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <img src="/images/mini-golf/MiniGolf-Priming-Parts.jpg" alt="Priming parts for the installation" />
                <span className="gallery-caption">Fabrication</span>
              </motion.div>
              <motion.div
                className="gallery-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <img src="/images/mini-golf/MiniGolf-Priming.jpeg" alt="Priming process" />
                <span className="gallery-caption">Priming</span>
              </motion.div>
              <motion.div
                className="gallery-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <img src="/images/mini-golf/MiniGolf-Me-Playtesting.JPG" alt="Playtesting the experience" />
                <span className="gallery-caption">Playtesting</span>
              </motion.div>
              <motion.div
                className="gallery-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <img src="/images/mini-golf/MiniGolf-Me-Playtesting2.JPG" alt="More playtesting" />
                <span className="gallery-caption">In Action</span>
              </motion.div>
              <motion.div
                className="gallery-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <img src="/images/mini-golf/MiniGolf-team.png" alt="The production team" />
                <span className="gallery-caption">The Team</span>
              </motion.div>
            </div>
          </AnimatedSection>

          {/* Reflection */}
          <AnimatedSection className="project-reflection" delay={0.4}>
            <div className="reflection-card">
              <h3>Reflections & Learnings</h3>
              <p>
                This project solidified my passion for immersive storytelling and taught me
                that the best experiences engage multiple senses and reward different levels
                of attention. The castle became a lesson in "weenies," the iconic elements
                that Disney Imagineers use to draw guests through space.
              </p>
              <p>
                More importantly, I learned that great themed entertainment requires balancing
                spectacular moments with intimate details, and that the best production
                managers are also advocates for the guest experience.
              </p>
              <p className="learning-note">
                If I were to do it again, I'd build in more time for user testing. Watching
                people discover hidden details we'd built (or miss wayfinding cues we
                thought were obvious) was invaluable learning I wish we'd had earlier.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Philanthropy Events */}
      <section className="project-section alt-bg">
        <div className="container">
          <AnimatedSection className="project-header playful">
            <span className="section-tag tag-pink">Event Production</span>
            <h2>
              Philanthropy<br/>
              <span className="title-italic">Initiatives</span>
            </h2>
            <p className="project-tagline">
              Tri Delta Fundraising Events | <em>Production & Design</em>
            </p>
          </AnimatedSection>

          <div className="project-content-simple">
            <AnimatedSection className="content-block" delay={0.1}>
              <p className="lead-text">
                As Director of Philanthropy for Tri Delta at USC, I raised over $75,000 for
                St. Jude Children's Research Hospital, winning Tri Delta National's Top Chapter
                Fundraising Award for the most raised by any chapter in a single year.
              </p>
            </AnimatedSection>

            <div className="event-cards">
              <AnimatedSection className="event-card" delay={0.2}>
                <div className="event-image">
                  <img src="/images/philanthropy/StJude.png" alt="St. Jude fundraising event" />
                </div>
                <div className="event-content">
                  <h4>St. Jude Week, Delta Dodgeball & DHOP</h4>
                  <p>
                    Planned and produced large-scale fundraising events from end to end:
                    creative design, prop fabrication, budgets, marketing graphics, permits,
                    and volunteer coordination for 200+ attendees.
                  </p>
                  <div className="event-meta">
                    <span>Event Production</span>
                    <span>Creative Design</span>
                    <span>$75K+ Raised</span>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection className="event-card" delay={0.3}>
                <div className="event-image">
                  <img src="/images/philanthropy/DHOP-Merch.png" alt="DHOP merch design" />
                </div>
                <div className="event-content">
                  <h4>Event Design & Production</h4>
                  <p>
                    Themed venue transformations, merch design, event marketing,
                    ticket sales strategy, and interactive guest experiences that
                    built community and drove participation.
                  </p>
                  <div className="event-meta">
                    <span>Spatial Design</span>
                    <span>Community Building</span>
                    <span>National Award</span>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Escape Room */}
      <section className="project-section">
        <div className="container">
          <AnimatedSection className="project-header playful">
            <span className="section-tag tag-cyan">Passion Project</span>
            <h2>
              Escape Room<br/>
              <span className="title-italic">Experiences</span>
            </h2>
            <p className="project-tagline">
              Game Design, Graphics & Operations | <em><strong>2023-2024</strong></em>
            </p>
          </AnimatedSection>

          <div className="project-content-simple">
            <AnimatedSection className="content-block" delay={0.1}>
              <p className="lead-text">
                As part of an escape room team, I contributed to multiple aspects
                of the experience, from storyline development and puzzle design
                to graphic design and live game master operations.
              </p>
            </AnimatedSection>

            <AnimatedSection className="escape-room-details" delay={0.2}>
              <div className="detail-grid">
                <div className="detail-card">
                  <h4>Storyline & Gameplay</h4>
                  <p>
                    Developed narrative arcs and puzzle sequences that created
                    satisfying "aha" moments while maintaining story coherence.
                  </p>
                </div>
                <div className="detail-card">
                  <h4>Clue & Puzzle Design</h4>
                  <p>
                    Created puzzles and clue systems that challenged players
                    without frustrating them, the sweet spot of escape room design.
                  </p>
                </div>
                <div className="detail-card">
                  <h4>Graphics & Props</h4>
                  <p>
                    Designed printed materials, signage, and visual elements that
                    reinforced the theme and supported puzzle mechanics.
                  </p>
                </div>
                <div className="detail-card">
                  <h4>Game Master Operations</h4>
                  <p>
                    Ran games as game master, learning to read player dynamics
                    and deliver hints that guide without solving.
                  </p>
                </div>
              </div>
              <div className="escape-image">
                <img
                  src="/images/escape-room/hero.png"
                  alt="Escape Room Experience"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA - Playful */}
      <section className="page-cta playful">
        <div className="container">
          <AnimatedSection className="cta-content-playful">
            <h2>See What Else I've Been Up To</h2>
            <p>
              From apps and interfaces to fan activations and pop-up concepts.
            </p>
            <div className="cta-links">
              <Link to="/experiential/digital" className="btn btn-primary">
                Digital & Interactive
              </Link>
              <Link to="/experiential/concepts" className="btn btn-primary">
                Concepts
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </motion.div>
  );
}

export default ImmersiveExperiences;
